import { mdiChevronDown, mdiChevronUp } from '@mdi/js';
import Icon from '@mdi/react';
import { Collapse, IconButton } from '@mui/material';
import {
  amountFormatter,
  commonButtonMessages,
  Dossier,
  useMyWalletDossierFilterLogic,
  walletDossierFiltersMessages,
  walletPageMessages,
} from 'azshared';
import React, { Fragment, MouseEvent, useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { AppButton, AppButtonType, AppButtonVariant } from '../../../../../components/buttons/app-button';
import { AppCheckbox } from '../../../../../components/checkbox/app-checkbox';
import { AppDialog } from '../../../../../components/dialog/app-dialog';
import { AppSvgIcon } from '../../../../../components/svg-icon/app-svg-icon';
import { AppTooltip } from '../../../../../components/tooltip/tooltip';
import './dossier-filter.scss';

export const DossierFilter = () => {
  const { formatMessage } = useIntl();

  const { onDossierFilterChange, dossierFilter, onSubmit, onCancel, setFlagOpenDossierFilter, flagOpenDossierFilter } = useMyWalletDossierFilterLogic();

  const handleClickOpenDossierFilter = useCallback(
    (e: MouseEvent<HTMLButtonElement | HTMLLabelElement>) => {
      e.preventDefault();
      setFlagOpenDossierFilter(true);
    },
    [setFlagOpenDossierFilter]
  );

  const handleKeyDownOpenDossierFilter = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement | HTMLLabelElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setFlagOpenDossierFilter(true);
      }
    },
    [setFlagOpenDossierFilter]
  );

  return (
    <Fragment>
      <div className='my-wallet-section-icon my-wallet-section-icon-filter'>
        <IconButton onClick={handleClickOpenDossierFilter}>
          <AppSvgIcon icon='filter' />
        </IconButton>
        <label onClick={handleClickOpenDossierFilter} onKeyDown={handleKeyDownOpenDossierFilter} tabIndex={0} className='my-wallet-section-icon-label'>
          {formatMessage(walletPageMessages.filterWallet)}
        </label>
      </div>
      <AppDialog open={flagOpenDossierFilter} onClose={onCancel} className='dossier-filter-dialog'>
        <div className='dossier-filter'>
          <div className='dossier-filter-header'>
            <div className='dossier-filter-title'>{formatMessage(walletDossierFiltersMessages.filterModalTitle)}</div>
          </div>
          <div className='dossier-filter-list'>
            {dossierFilter?.dossier?.map((dossier, index) => (
              <DossierFilterDialogItem dossier={dossier} key={`${index}_${dossier.code}`} onFilterChange={onDossierFilterChange} />
            ))}
          </div>
          <div className='dossier-filter-button-section'>
            <AppButton type={AppButtonType.BUTTON} variant={AppButtonVariant.Secondary} label={formatMessage(commonButtonMessages.cancel)} onClick={onCancel} />
            <AppButton
              type={AppButtonType.BUTTON}
              variant={AppButtonVariant.Primary}
              label={formatMessage(walletDossierFiltersMessages.filterRecalculateButton)}
              onClick={onSubmit}
              disabled={dossierFilter?.dossier?.length === 0}
            />
          </div>
        </div>
      </AppDialog>
    </Fragment>
  );
};

interface DossierFilterDialogItemProps {
  dossier: Dossier;
  onFilterChange: (code: string, value: boolean) => void;
}

export const DossierFilterDialogItem = (props: DossierFilterDialogItemProps) => {
  const { dossier, onFilterChange } = props;
  const [open, setOpen] = useState(false);
  const toggleOpen = useCallback(() => setOpen((prev) => !prev), []);
  const { formatMessage } = useIntl();

  const handleChecked = useCallback(
    (value: boolean, code?: string) => {
      onFilterChange(code || '', value);
    },
    [onFilterChange]
  );

  const label = useMemo(() => {
    let baseLabel = dossier.code;
    if (dossier.consultingType) {
      baseLabel = [dossier.code, dossier.consultingType.type, dossier.consultingType.contract].filter((property) => property?.length > 0).join(' - ');
    }
    const extraInfo = [];
    if (dossier.commissionRegimeTypeDescription) {
      extraInfo.push(dossier.commissionRegimeTypeDescription);
    }
    if (dossier.controvalore && dossier.controvalore.value !== -1) {
      extraInfo.push(amountFormatter(dossier.controvalore));
    }
    return extraInfo.length > 0 ? `${baseLabel} (${extraInfo.join(' - ')})` : baseLabel;
  }, [dossier.consultingType, dossier.code, dossier.controvalore, dossier.commissionRegimeTypeDescription]);

  const hasSubDossier = Boolean(dossier.subDossier && dossier.subDossier.length > 0);

  return (
    <div className={`dossier-filter-items ${dossier.flagSelected ? 'is-selected' : ''}`}>
      <div className='dossier-filter-item'>
        <AppTooltip title={formatMessage(dossier.flagOpen ? walletDossierFiltersMessages.openTooltip : walletDossierFiltersMessages.closeTooltip)}>
          <div className={`dossier-filter-dot ${dossier.flagOpen ? 'open' : ''}`} />
        </AppTooltip>
        <div className='dossier-filter-item-label'>
          <AppCheckbox id={dossier.code} label={label} checked={dossier.flagSelected} onChange={handleChecked} />
          {dossier.coOwner && dossier.coOwner.length > 0 && (
            <div className='coowner'>
              {dossier.coOwner.map((coowner, index) => (
                <div key={coowner.code || `${coowner.name}-${index}`} className='coowner-label'>
                  <AppSvgIcon icon={'coowner'} />
                  <span>{[coowner.name, coowner.code].filter(Boolean).join(' - ')}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        {hasSubDossier && (
          <IconButton
            aria-label='expand row'
            size='small'
            onClick={toggleOpen}
            className={`dossier-filter-accordion-icon ${open ? 'expanded' : ''}`}
          >
            <Icon size={1} path={open ? mdiChevronUp : mdiChevronDown} color={'#2E6CD1'} />
          </IconButton>
        )}
      </div>
      {hasSubDossier && (
        <div className={open ? 'is-expansion-visible' : ''}>
          <Collapse in={open} timeout='auto' unmountOnExit className='dossier-filter-accordion-box'>
            <div className='dossier-filter-accordion-box-content'>
              {dossier.subDossier!.map((subDossier) => {
                const parts = [subDossier.suppCompanyRefNum];
                if (subDossier.macroProductName) parts.push(subDossier.macroProductName);
                if (subDossier.productName) parts.push(subDossier.productName);
                if (subDossier.commissionRegimeTypeDescription && subDossier.macroProductName) {
                  parts.push(subDossier.commissionRegimeTypeDescription);
                }
                if (subDossier.controvalore && subDossier.controvalore.value !== -1) {
                  parts.push(amountFormatter(subDossier.controvalore));
                }
                return (
                  <div key={subDossier.contract} className='dossier-filter-accordion-item'>
                    <div className='dossier-filter-item-label'>
                      <AppCheckbox
                        id={subDossier.contract}
                        label={parts.join(' - ')}
                        checked={subDossier.flagSelected}
                        onChange={handleChecked}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </Collapse>
        </div>
      )}
    </div>
  );
};
